import { Howl } from 'howler';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';

import * as digitalData from '@ao-internal/digital-data';

import { ChatSession } from '../App';
import agentConnectedSoundMp3 from '../assets/sounds/agentConnected.mp3';
import agentConnectedSoundWebm from '../assets/sounds/agentConnected.webm';
import newMessageSoundMp3 from '../assets/sounds/newMessage.mp3';
import newMessageSoundWebm from '../assets/sounds/newMessage.webm';
import {
	CONVERSATION_STARTER,
	LIVECHAT_CLOSING_QUESTION_INTENT,
	LIVE_CHAT_INACTIVITY_INTENT,
	tabId,
} from '../globals';
import LiveChatHandler, { PollResponse } from '../handlers/LiveChatHandler';
import { logError } from '../helpers/logging';
import {
	getLastConnectedAgentFromHistory,
	getLastMessageGroupContainingAnIntent,
	getLastMessageOfTypeFromChatHistory,
	getLastSentMessageFromHistory,
	getMessageFromMessageGroupByMessageId,
	getMessageGroupByMessageId,
} from '../helpers/state';
import {
	getRefreshStateFlagCookie,
	setRefreshStateFlagCookie,
} from '../helpers/storage';
import * as tabManager from '../helpers/tab';
import usePrevious from '../hooks/usePrevious';
import { BaseMessageSender } from '../message-senders/BaseMessageSender';
import MessageSenderFactory from '../message-senders/MessageSenderFactory';
import {
	ActivateLiveChatChatMessage,
	ActivateLiveChatContentType,
	ChatMessageStatusTypes,
	ChatMessageTypes,
	ChatMode,
	ChatWindowStatusTypes,
	InputTypes,
	LiveChatStartChatMessage,
	LiveChatStatus,
	SenderTypes,
	SimpleMessageContentType,
	useChatDispatch,
	useChatState,
} from '../store';
import useRefreshChatSession from './useRefreshChatSession';

let messageSender: BaseMessageSender;
let liveChatClosingEvent = LIVE_CHAT_INACTIVITY_INTENT;
let hasFiredLiveChatClosingEvent = false;
let firstInstanceId: string | undefined;
let initialiseChatRunning = false;

const agentConnectedSoundNotification = new Howl({
	src: [agentConnectedSoundMp3, agentConnectedSoundWebm],
	format: ['mp3', 'webm'],
});
const newMessageSoundNotification = new Howl({
	src: [newMessageSoundMp3, newMessageSoundWebm],
	format: ['mp3', 'webm'],
});

// TODO: Define interface
export function useChat() {
	const {
		setSessionId,
		setChatHistory,
		deleteUserMessage,
		setMessageStatus,
		setShowTypingIndicator,
		addMessageToChatHistory,
		setResults,
		setResponseMetaData,
		setChatWindowStatus,
		setSessionInitialised,
		setChatInitialised,
		setLiveChatActivationData,
		setChatMode,
		setLiveChatStatus,
		setLiveChatAgent,
		disconnectLiveChat,
		setResponsesToProcess,
		setLiveChatExitedBy,
		setLiveChatAutoDisconnectNoticeReceivedTimestamp,
		setLiveChatAutoDisconnectNoticeDismissedTimestamp,
	} = useChatDispatch();
	const globalState = useChatState();
	const {
		sessionId,
		chatMode,
		chatHistory,
		chatWindowStatus,
		userInputValue,
		liveChatStatus,
		responsesToProcess,
		isTabVisible,
		lastChatbotMessageType,
		responseMetaData,
	} = globalState;
	const prevLiveChatStatus = usePrevious(liveChatStatus);
	const [sendMessageError, setSendMessageError] = React.useState(false);
	const [useChatId] = React.useState(uuidv4());

	function setTitle(title: string) {
		document.title = title;
	}

	const dismissLiveChatAutoDisconnectNotice = React.useCallback(() => {
		setLiveChatAutoDisconnectNoticeDismissedTimestamp(Date.now());
	}, [setLiveChatAutoDisconnectNoticeDismissedTimestamp]);

	const sendMessage = React.useCallback(
		async (
			message: SimpleMessageContentType,
			{
				id,
				addToHistory = true,
				isSystemMessage = false,
				inputType = InputTypes.Text,
				groupIntent,
				isInitialMessage = false,
			}: {
				id?: string;
				addToHistory?: boolean;
				isSystemMessage?: boolean;
				inputType?: InputTypes;
				groupIntent?: string;
				isInitialMessage?: boolean;
			} = {}
		): Promise<void> => {
			const messageAlreadyExistsInChatHistory = !!id;
			const messageId = id ? id : uuidv4();
			const senderType = isSystemMessage
				? SenderTypes.System
				: SenderTypes.User;

			if (chatWindowStatus === ChatWindowStatusTypes.Error) {
				setChatWindowStatus(ChatWindowStatusTypes.Loading);
			}

			if (messageAlreadyExistsInChatHistory) {
				setMessageStatus(messageId, ChatMessageStatusTypes.Pending);
			}

			if (addToHistory) {
				addMessageToChatHistory({
					sender: senderType,
					messageData: {
						id: messageId,
						status: ChatMessageStatusTypes.Pending,
						type: ChatMessageTypes.Simple,
						content: message,
						inputType,
					},
				});
			}

			if (chatMode === ChatMode.ChatBot) {
				setShowTypingIndicator(true);
			}

			await messageSender
				.sendMessage({
					sessionId,
					message,
					isSystemMessage,
					inputType,
					groupIntent,
				})
				.then((responseData) => {
					const {
						sessionId: currentSessionId,
						results,
						responses,
						intent: replyIntent,
						confidenceScore,
					} = responseData;
					let messageStatus = ChatMessageStatusTypes.Sent;

					setSessionId(currentSessionId);
					setResults(results);

					const responseMeta = replyIntent
						? { intent: replyIntent, confidenceScore }
						: undefined;
					setResponseMetaData(responseMeta);

					setResponsesToProcess(responses);

					setChatWindowStatus(ChatWindowStatusTypes.Okay);
					if (!responses.length && chatMode === ChatMode.ChatBot) {
						messageStatus = ChatMessageStatusTypes.Failed;
						logError('Chatbot responses empty (useChat: sendMessage)', {
							message,
							isSystemMessage,
							inputType,
						});
					}
					if (!isSystemMessage) {
						setMessageStatus(messageId, messageStatus);
					}

					dismissLiveChatAutoDisconnectNotice();

					if (
						isInitialMessage &&
						messageStatus === ChatMessageStatusTypes.Failed
					) {
						setChatWindowStatus(ChatWindowStatusTypes.Error);
					}
				})
				.catch(() => {
					// TODO: Log this error within the sender class
					setSendMessageError(true);
					setMessageStatus(messageId, ChatMessageStatusTypes.Failed);

					if (isInitialMessage) {
						setChatWindowStatus(ChatWindowStatusTypes.Error);
					} else {
						setChatWindowStatus(ChatWindowStatusTypes.Okay);
					}
				})
				.finally(() => {
					setChatInitialised(true);
					if (chatMode === ChatMode.ChatBot) {
						setShowTypingIndicator(false);
					}
					setSendMessageError(false);
				});

			return Promise.resolve();
		},
		[
			addMessageToChatHistory,
			chatMode,
			chatWindowStatus,
			dismissLiveChatAutoDisconnectNotice,
			sessionId,
			setChatInitialised,
			setChatWindowStatus,
			setMessageStatus,
			setResponseMetaData,
			setResponsesToProcess,
			setResults,
			setSessionId,
			setShowTypingIndicator,
		]
	);

	const resendMessage = React.useCallback(
		async (messageId: string) => {
			// Get the most up-to-date data for this message
			const groupData = getMessageGroupByMessageId(messageId, chatHistory);

			if (!groupData) {
				return;
			}

			const messageData = getMessageFromMessageGroupByMessageId(
				messageId,
				groupData
			);

			if (!messageData) {
				return;
			}

			const { content, type, inputType } = messageData;

			if (type !== ChatMessageTypes.Simple || typeof content !== 'string') {
				return;
			}

			deleteUserMessage(messageId);
			await sendMessage(content, {
				inputType,
				groupIntent: groupData.intent,
			});
		},
		[chatHistory, deleteUserMessage, sendMessage]
	);

	const startLiveChat = React.useCallback(
		async (activationData: ActivateLiveChatContentType): Promise<void> => {
			setLiveChatAutoDisconnectNoticeReceivedTimestamp(undefined);
			setLiveChatAutoDisconnectNoticeDismissedTimestamp(undefined);
			setChatMode(ChatMode.LiveChat);
			setLiveChatStatus(LiveChatStatus.Connecting);
			setLiveChatExitedBy(undefined);

			try {
				await LiveChatHandler.start(activationData, (response: PollResponse) =>
					setResponsesToProcess(response.responses)
				);
			} catch (error) {
				setLiveChatStatus(LiveChatStatus.ConnectionError);
			}
		},
		[
			setChatMode,
			setLiveChatAutoDisconnectNoticeDismissedTimestamp,
			setLiveChatAutoDisconnectNoticeReceivedTimestamp,
			setLiveChatExitedBy,
			setLiveChatStatus,
			setResponsesToProcess,
		]
	);

	const exitLiveChat = React.useCallback(
		async (exitedBy: SenderTypes, shouldCallExitEndpoint = true) => {
			try {
				await LiveChatHandler.exit(shouldCallExitEndpoint);
			} catch (error) {
				// TODO: Handle failure to exit and log failure!
			}

			if (exitedBy === SenderTypes.User) {
				addMessageToChatHistory({
					sender: SenderTypes.User,
					messageData: {
						id: uuidv4(),
						type: ChatMessageTypes.LiveChatExit,
						inputType: InputTypes.Undefined,
					},
				});
			}

			setLiveChatAutoDisconnectNoticeReceivedTimestamp(undefined);
			setLiveChatAutoDisconnectNoticeDismissedTimestamp(undefined);
			setLiveChatExitedBy(exitedBy);
			disconnectLiveChat();
		},
		[
			addMessageToChatHistory,
			disconnectLiveChat,
			setLiveChatAutoDisconnectNoticeDismissedTimestamp,
			setLiveChatAutoDisconnectNoticeReceivedTimestamp,
			setLiveChatExitedBy,
		]
	);

	const initSession = React.useCallback(
		async (chatSession: ChatSession) => {
			const {
				sessionId: currentSessionId,
				chatHistory: history,
				results,
				liveChatAutoDisconnectNoticeReceivedTimestamp,
				liveChatAutoDisconnectNoticeDismissedTimestamp,
			} = chatSession;

			setLiveChatAutoDisconnectNoticeReceivedTimestamp(
				liveChatAutoDisconnectNoticeReceivedTimestamp
			);
			setLiveChatAutoDisconnectNoticeDismissedTimestamp(
				liveChatAutoDisconnectNoticeDismissedTimestamp
			);

			const activateLiveChatResponse = getLastMessageOfTypeFromChatHistory(
				history,
				ChatMessageTypes.ActivateLiveChat
			) as ActivateLiveChatChatMessage;

			if (activateLiveChatResponse) {
				setLiveChatActivationData(activateLiveChatResponse.content);
			}

			if (
				chatSession.liveChatStatus === LiveChatStatus.Connecting ||
				chatSession.liveChatStatus === LiveChatStatus.Connected ||
				chatSession.liveChatStatus === LiveChatStatus.Transferring
			) {
				const liveChatStartResponse = getLastMessageOfTypeFromChatHistory(
					history,
					ChatMessageTypes.LiveChatStart
				) as LiveChatStartChatMessage;

				if (currentSessionId && liveChatStartResponse) {
					liveChatClosingEvent = LIVECHAT_CLOSING_QUESTION_INTENT;
					setChatMode(ChatMode.LiveChat);
					setLiveChatStatus(chatSession.liveChatStatus);
					if (chatSession.liveChatStatus === LiveChatStatus.Connected) {
						const agent = getLastConnectedAgentFromHistory(history);
						if (agent) {
							setLiveChatAgent(agent);
						}
					}
					const startData = liveChatStartResponse.content;
					LiveChatHandler.resume(
						{
							sessionId: currentSessionId,
							participantId: startData.participantId,
						},
						(response: PollResponse) =>
							setResponsesToProcess(response.responses)
					);
				}
			} else if (
				chatSession.liveChatStatus === LiveChatStatus.ConnectionError
			) {
				setChatMode(ChatMode.LiveChat);
				setLiveChatStatus(chatSession.liveChatStatus);
			} else if (
				liveChatStatus !== undefined &&
				liveChatStatus !== LiveChatStatus.Disconnected
			) {
				LiveChatHandler.exit();
				setChatMode(ChatMode.ChatBot);
				setLiveChatAgent(undefined);
				setLiveChatAutoDisconnectNoticeReceivedTimestamp(undefined);
				setLiveChatAutoDisconnectNoticeDismissedTimestamp(undefined);
				setLiveChatStatus(undefined);
			}

			// TODO: only update sessionId if it has changed?
			if (currentSessionId) {
				setSessionId(currentSessionId);
				digitalData.manipulateDigitalData({
					page: {
						pageInfo: {
							chatbot: {
								sessionId: currentSessionId,
							},
						},
					},
				});
			}

			setResults(results);

			if (history) {
				setChatHistory(history);
				setChatInitialised(true);
				setShowTypingIndicator(false);
			}

			setSessionInitialised(true);
		},
		[
			liveChatStatus,
			setChatHistory,
			setChatInitialised,
			setChatMode,
			setLiveChatActivationData,
			setLiveChatAgent,
			setLiveChatAutoDisconnectNoticeDismissedTimestamp,
			setLiveChatAutoDisconnectNoticeReceivedTimestamp,
			setLiveChatStatus,
			setResponsesToProcess,
			setResults,
			setSessionId,
			setSessionInitialised,
			setShowTypingIndicator,
		]
	);

	const refreshChatSession = useRefreshChatSession({
		state: globalState,
		callback: initSession,
	});

	const initialiseChat = React.useCallback(
		async (
			message: string,
			isSystemMessage?: boolean,
			inputType: InputTypes = InputTypes.Text
		): Promise<void> => {
			if (!initialiseChatRunning) {
				initialiseChatRunning = true;

				setChatWindowStatus(ChatWindowStatusTypes.Loading);

				if (chatHistory.length) {
					const lastSentMessage = getLastSentMessageFromHistory(chatHistory);
					const lastChatGroupWithIntent =
						getLastMessageGroupContainingAnIntent(chatHistory);

					// TODO: In the event that we find a message which failed to send
					// we should resend it using the appropriate inputType
					// e.g.
					// 1) initially open chatbot using tooltip and message fails to send
					// 2) click "Reload" button (on the chatbot error screen)
					// 3) error screen disappears, and message has been sent, requiring another click

					if (
						lastSentMessage &&
						lastSentMessage.content !== message &&
						CONVERSATION_STARTER !== message
					) {
						await sendMessage(message, {
							isSystemMessage,
							addToHistory: !isSystemMessage,
							inputType,
							groupIntent: lastChatGroupWithIntent
								? lastChatGroupWithIntent.intent
								: undefined,
						});
					} else {
						// Don't bother repeating the same message
						setChatWindowStatus(ChatWindowStatusTypes.Okay);
					}
				} else {
					await sendMessage(message, {
						isSystemMessage,
						addToHistory: !isSystemMessage,
						inputType,
						isInitialMessage: true,
					});
				}

				// eslint-disable-next-line require-atomic-updates
				initialiseChatRunning = false;

				return Promise.resolve();
			}

			return Promise.resolve();
		},
		[chatHistory, sendMessage, setChatWindowStatus]
	);

	React.useEffect(() => {
		if (firstInstanceId) {
			return () => null;
		}
		firstInstanceId = useChatId;

		return () => {
			liveChatClosingEvent = LIVE_CHAT_INACTIVITY_INTENT;
			hasFiredLiveChatClosingEvent = false;
			firstInstanceId = undefined;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (firstInstanceId !== useChatId) {
			return;
		}

		if (!responsesToProcess.length) {
			if (getRefreshStateFlagCookie()) {
				refreshChatSession();
				setRefreshStateFlagCookie(false);
			}
			return;
		}

		// TODO: Find a nicer fix: The global state doesn't update quickly enough, so a local variable is required
		let isTransferring = liveChatStatus === LiveChatStatus.Transferring;

		responsesToProcess.forEach((response) => {
			let senderType = SenderTypes.Chatbot;

			switch (response.type) {
				case ChatMessageTypes.ActivateLiveChat:
					setLiveChatActivationData(response.content);
					setLiveChatStatus(LiveChatStatus.Connecting);
					setChatMode(ChatMode.LiveChat);

					startLiveChat(response.content);

					liveChatClosingEvent = LIVECHAT_CLOSING_QUESTION_INTENT;
					hasFiredLiveChatClosingEvent = false;
					break;

				case ChatMessageTypes.LiveChatAgentStateChange:
					senderType = SenderTypes.LiveChat;

					if (response.content.state === 'active') {
						setLiveChatStatus(LiveChatStatus.Connected);
						setLiveChatAgent({
							name: response.content.name,
							initials: response.content.initials,
							participantId: response.senderInfo
								? response.senderInfo.participantId
								: undefined,
							isConnected: true,
						});
						if (!isTabVisible) {
							const segments = response.content.name.split(' ');
							const name = segments && segments.length ? segments[0] : 'Agent';
							setTitle(`Connected to ${name}`);
							agentConnectedSoundNotification.play();
						}
					} else if (!isTransferring) {
						exitLiveChat(SenderTypes.LiveChat);
					}
					break;

				case ChatMessageTypes.LiveChatTypingIndicator:
					senderType = SenderTypes.LiveChat;
					setShowTypingIndicator(response.content.isTyping);
					break;

				case ChatMessageTypes.LiveChatAgentTransferInitiate:
					senderType = SenderTypes.LiveChat;
					setLiveChatStatus(LiveChatStatus.Transferring);
					isTransferring = true;
					break;

				case ChatMessageTypes.LiveChatAutoDisconnectNotice:
					senderType = SenderTypes.LiveChat;
					setLiveChatAutoDisconnectNoticeDismissedTimestamp(undefined);
					setLiveChatAutoDisconnectNoticeReceivedTimestamp(Date.now());
					if (!isTabVisible) {
						setTitle('New Message');
						newMessageSoundNotification.play();
					}
					break;

				case ChatMessageTypes.LiveChatAutoDisconnection:
					senderType = SenderTypes.LiveChat;
					liveChatClosingEvent = LIVE_CHAT_INACTIVITY_INTENT;
					exitLiveChat(SenderTypes.System, false);
					break;

				default:
					senderType =
						chatMode === ChatMode.ChatBot
							? SenderTypes.Chatbot
							: SenderTypes.LiveChat;
					if (!isTabVisible) {
						setTitle('New Message');
						newMessageSoundNotification.play();
					}
					break;
			}

			addMessageToChatHistory({
				sender: senderType,
				messageData: response,
				intent: responseMetaData ? responseMetaData.intent : undefined, // TODO:
			});
		});

		setResponsesToProcess([]);

		const stolenResponses = !isTabVisible && !tabManager.isActiveTab(tabId);
		if (stolenResponses) {
			setRefreshStateFlagCookie(true);
		}
	}, [
		addMessageToChatHistory,
		chatMode,
		exitLiveChat,
		isTabVisible,
		liveChatStatus,
		refreshChatSession,
		responseMetaData,
		responsesToProcess,
		setChatMode,
		setLiveChatActivationData,
		setLiveChatAgent,
		setLiveChatAutoDisconnectNoticeDismissedTimestamp,
		setLiveChatAutoDisconnectNoticeReceivedTimestamp,
		setLiveChatStatus,
		setResponsesToProcess,
		setShowTypingIndicator,
		startLiveChat,
		useChatId,
	]);

	React.useEffect(() => {
		LiveChatHandler.userDidType(userInputValue);
	}, [userInputValue]);

	React.useEffect(() => {
		messageSender = MessageSenderFactory.create(chatMode);
	}, [chatMode]);

	React.useEffect(() => {
		if (sendMessageError && !chatHistory.length) {
			setChatWindowStatus(ChatWindowStatusTypes.Error);
			setSendMessageError(false);
		}
	}, [chatHistory, sendMessageError, setChatWindowStatus, setSendMessageError]);

	const mostRecentChatMessageIsLiveChatDisconnection =
		React.useMemo((): boolean => {
			// conversation history is empty
			// no live chat conversation
			// live chat conversation exists and hasn't been disconnected
			// live chat conversation exists and has been disconnected
			// multiple live chat conversations in the history with one still active
			// multiple live chat conversations in the history with none active
			if (Array.isArray(chatHistory)) {
				const lastChatGroup = chatHistory[chatHistory.length - 1];
				if (lastChatGroup) {
					const lastChatMessage =
						lastChatGroup.messages[lastChatGroup.messages.length - 1];

					return (
						lastChatMessage.type ===
							ChatMessageTypes.LiveChatAutoDisconnection ||
						lastChatMessage.type === ChatMessageTypes.LiveChatExit ||
						(lastChatMessage.type ===
							ChatMessageTypes.LiveChatAgentStateChange &&
							lastChatMessage.content &&
							lastChatMessage.content.state === 'disconnected')
					);
				}
				return false;
			}
			return false;
		}, [chatHistory]);

	React.useEffect(() => {
		if (
			!hasFiredLiveChatClosingEvent &&
			liveChatStatus === LiveChatStatus.Disconnected &&
			prevLiveChatStatus !== liveChatStatus &&
			mostRecentChatMessageIsLiveChatDisconnection
		) {
			hasFiredLiveChatClosingEvent = true;
			setLiveChatStatus(undefined);

			sendMessage(liveChatClosingEvent, {
				addToHistory: false,
				isSystemMessage: true,
				inputType: InputTypes.Undefined,
			});
		}
	}, [
		lastChatbotMessageType,
		liveChatStatus,
		mostRecentChatMessageIsLiveChatDisconnection,
		prevLiveChatStatus,
		sendMessage,
		setLiveChatStatus,
	]);

	return {
		initSession,
		initialiseChat,
		sendMessage,
		resendMessage,
		startLiveChat,
		exitLiveChat,
		dismissLiveChatAutoDisconnectNotice,
		refreshChatSession,
	};
}

export default useChat;
